'use strict'

const KMZ_SPEED_SCOPE_PRIORITY = {
  playback: ['waylines', 'template']
}

const KMZ_SPEED_ROOT_CACHE_PATHS = [
  'wpml:useGlobalSpeed',
  'useGlobalSpeed',
  'wpml:waypointSpeed',
  'waypointSpeed',
  'wpml:autoFlightSpeed',
  'autoFlightSpeed',
  'Folder.wpml:autoFlightSpeed',
  'Folder.autoFlightSpeed'
]

const KMZ_SPEED_SCOPED_CACHE_PATHS = [
  'wpml:useGlobalSpeed',
  'useGlobalSpeed',
  'wpml:waypointSpeed',
  'waypointSpeed',
  'wpml:autoFlightSpeed',
  'autoFlightSpeed',
  'Folder.wpml:autoFlightSpeed',
  'Folder.autoFlightSpeed'
]

const fnCloneSpeedValue = function (value) {
  if (Array.isArray(value)) {
    return value.map(fnCloneSpeedValue)
  }
  if (value && typeof value === 'object') {
    const out = {}
    Object.keys(value).forEach((key) => {
      out[key] = fnCloneSpeedValue(value[key])
    })
    return out
  }
  return value
}

const fnGetByPath = function (obj, path) {
  const parts = String(path || '').split('.').filter(Boolean)
  let current = obj
  for (let i = 0; i < parts.length; i += 1) {
    if (!current || typeof current !== 'object') return undefined
    current = current[parts[i]]
  }
  return current
}

const fnSetByPath = function (obj, path, value) {
  const parts = String(path || '').split('.').filter(Boolean)
  if (!parts.length || !obj || typeof obj !== 'object') return
  let current = obj
  for (let i = 0; i < parts.length - 1; i += 1) {
    const key = parts[i]
    if (!current[key] || typeof current[key] !== 'object' || Array.isArray(current[key])) {
      current[key] = {}
    }
    current = current[key]
  }
  current[parts[parts.length - 1]] = fnCloneSpeedValue(value)
}

const fnDeletePath = function (obj, path) {
  const parts = String(path || '').split('.').filter(Boolean)
  if (!parts.length || !obj || typeof obj !== 'object') return
  const stack = [{ parent: null, key: '', value: obj }]
  let current = obj
  for (let i = 0; i < parts.length; i += 1) {
    if (!current || typeof current !== 'object') return
    const key = parts[i]
    if (!Object.prototype.hasOwnProperty.call(current, key)) return
    current = current[key]
    stack.push({ parent: stack[stack.length - 1].value, key: key, value: current })
  }
  const leaf = stack.pop()
  if (!leaf || !leaf.parent || !leaf.key) return
  delete leaf.parent[leaf.key]
  while (stack.length > 1) {
    const node = stack.pop()
    if (!node || !node.parent || !node.key) continue
    const value = node.parent[node.key]
    if (!value || typeof value !== 'object' || Array.isArray(value) || Object.keys(value).length) break
    delete node.parent[node.key]
  }
}

const fnPickFirstDefined = function (source, paths) {
  const safeSource = (source && typeof source === 'object') ? source : {}
  const list = Array.isArray(paths) ? paths : []
  for (let i = 0; i < list.length; i += 1) {
    const path = String(list[i] || '').trim()
    if (!path) continue
    const value = fnGetByPath(safeSource, path)
    if (value === undefined) continue
    return value
  }
  return undefined
}

const fnToFiniteNumber = function (value) {
  if (value === null || value === undefined) return null
  if (typeof value === 'string' && !String(value).trim()) return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

const fnParseBoolLike = function (value) {
  if (value === null || value === undefined) return null
  if (typeof value === 'boolean') return value
  const text = String(value).trim().toLowerCase()
  if (!text) return null
  if (text === '1' || text === 'true' || text === 'yes' || text === 'on') return true
  if (text === '0' || text === 'false' || text === 'no' || text === 'off') return false
  const numeric = Number(text)
  if (Number.isFinite(numeric)) return numeric !== 0
  return null
}

const fnAssignScopedSpeedSource = function (target, targetPath, source, sourcePaths) {
  const value = fnPickFirstDefined(source, sourcePaths)
  if (value === undefined) return
  fnSetByPath(target, targetPath, value)
}

const fnBuildScopeSpeedSource = function (pointProps, folderProps) {
  const out = {}
  fnAssignScopedSpeedSource(out, 'wpml:useGlobalSpeed', pointProps, [
    'wpml:useGlobalSpeed',
    'useGlobalSpeed'
  ])
  fnAssignScopedSpeedSource(out, 'wpml:waypointSpeed', pointProps, [
    'wpml:waypointSpeed',
    'waypointSpeed'
  ])
  fnAssignScopedSpeedSource(out, 'Folder.wpml:autoFlightSpeed', pointProps, [
    'Folder.wpml:autoFlightSpeed',
    'Folder.autoFlightSpeed',
    'wpml:autoFlightSpeed',
    'autoFlightSpeed'
  ])
  fnAssignScopedSpeedSource(out, 'Folder.wpml:autoFlightSpeed', folderProps, [
    'wpml:autoFlightSpeed',
    'autoFlightSpeed',
    'Folder.wpml:autoFlightSpeed',
    'Folder.autoFlightSpeed'
  ])
  return out
}

const fnPruneKmzSpeedCache = function (props) {
  const safeProps = (props && typeof props === 'object') ? props : null
  if (!safeProps) return
  KMZ_SPEED_ROOT_CACHE_PATHS.forEach((path) => {
    fnDeletePath(safeProps, path)
  })
  ;['template', 'waylines'].forEach((scopeId) => {
    KMZ_SPEED_SCOPED_CACHE_PATHS.forEach((path) => {
      fnDeletePath(safeProps, scopeId + '.' + path)
    })
  })
}

const buildKmzScopedSpeedPropsFromSources = function ({
  templatePointProps,
  waylinePointProps,
  templateFolderProps,
  waylinesFolderProps
} = {}) {
  return {
    template: fnBuildScopeSpeedSource(templatePointProps, templateFolderProps),
    waylines: fnBuildScopeSpeedSource(waylinePointProps, waylinesFolderProps)
  }
}

const applyKmzScopedSpeedProps = function (props, scopedSpeedProps) {
  const safeProps = (props && typeof props === 'object') ? props : null
  if (!safeProps) return safeProps
  const scoped = (scopedSpeedProps && typeof scopedSpeedProps === 'object') ? scopedSpeedProps : {}
  fnPruneKmzSpeedCache(safeProps)
  ;['template', 'waylines'].forEach((scopeId) => {
    const scopeProps = scoped[scopeId]
    if (!scopeProps || typeof scopeProps !== 'object') return
    fnAssignScopedSpeedSource(safeProps, scopeId + '.wpml:useGlobalSpeed', scopeProps, [
      'wpml:useGlobalSpeed',
      'useGlobalSpeed'
    ])
    fnAssignScopedSpeedSource(safeProps, scopeId + '.wpml:waypointSpeed', scopeProps, [
      'wpml:waypointSpeed',
      'waypointSpeed'
    ])
    fnAssignScopedSpeedSource(safeProps, scopeId + '.Folder.wpml:autoFlightSpeed', scopeProps, [
      'Folder.wpml:autoFlightSpeed',
      'Folder.autoFlightSpeed',
      'wpml:autoFlightSpeed',
      'autoFlightSpeed'
    ])
  })
  return safeProps
}

const syncKmzPointSpeedScopesFromXml = function (point, {
  fnXmlToObj,
  templateFolderProps,
  waylinesFolderProps,
  templateFolderEl,
  waylinesFolderEl
} = {}) {
  if (!point || typeof fnXmlToObj !== 'function') return point
  if (!point.props || typeof point.props !== 'object') point.props = {}
  const templatePointProps = point.xmlEl ? fnXmlToObj(point.xmlEl) : {}
  const waylinePointEl = Array.isArray(point.linkedXmlEls) ? (point.linkedXmlEls[0] || null) : null
  const waylinePointProps = waylinePointEl ? fnXmlToObj(waylinePointEl) : {}
  const scoped = buildKmzScopedSpeedPropsFromSources({
    templatePointProps: templatePointProps,
    waylinePointProps: waylinePointProps,
    templateFolderProps: templateFolderProps || (templateFolderEl ? fnXmlToObj(templateFolderEl) : {}),
    waylinesFolderProps: waylinesFolderProps || (waylinesFolderEl ? fnXmlToObj(waylinesFolderEl) : {})
  })
  applyKmzScopedSpeedProps(point.props, scoped)
  return point
}

const syncKmzMissionSpeedScopesFromXml = function (missionState, fnXmlToObj) {
  if (!missionState || !Array.isArray(missionState.waypoints) || typeof fnXmlToObj !== 'function') return missionState
  const templateFolderProps = missionState.folderEl ? fnXmlToObj(missionState.folderEl) : {}
  const waylinesFolderProps = missionState.secondaryFolderEl ? fnXmlToObj(missionState.secondaryFolderEl) : {}
  missionState.waypoints.forEach((point) => {
    syncKmzPointSpeedScopesFromXml(point, {
      fnXmlToObj: fnXmlToObj,
      templateFolderProps: templateFolderProps,
      waylinesFolderProps: waylinesFolderProps
    })
  })
  return missionState
}

const fnNormalizeSpeedPathForCheck = function (path) {
  let normalized = String(path || '').trim()
  if (!normalized) return ''
  if (normalized.toLowerCase().indexOf('template.') === 0) normalized = normalized.slice(9)
  if (String(normalized).toLowerCase().indexOf('waylines.') === 0) normalized = normalized.slice(9)
  return String(normalized || '').trim().toLowerCase()
}

const isKmzSpeedFieldPath = function (path) {
  const normalized = fnNormalizeSpeedPathForCheck(path)
  if (!normalized) return false
  return normalized === 'wpml:useglobalspeed' ||
    normalized === 'useglobalspeed' ||
    normalized === 'wpml:waypointspeed' ||
    normalized === 'waypointspeed' ||
    normalized === 'wpml:autoflightspeed' ||
    normalized === 'autoflightspeed' ||
    normalized === 'folder.wpml:autoflightspeed' ||
    normalized === 'folder.autoflightspeed'
}

const isKmzGlobalSpeedFieldPath = function (path) {
  const normalized = fnNormalizeSpeedPathForCheck(path)
  if (!normalized) return false
  return normalized === 'wpml:autoflightspeed' ||
    normalized === 'autoflightspeed' ||
    normalized === 'folder.wpml:autoflightspeed' ||
    normalized === 'folder.autoflightspeed'
}

const fnResolveSpeedCandidateByUseCase = function (pointOrProps, useCase, mode, fallbackValue) {
  const point = (pointOrProps && pointOrProps.props && typeof pointOrProps.props === 'object') ? pointOrProps : null
  const props = point ? point.props : ((pointOrProps && typeof pointOrProps === 'object') ? pointOrProps : {})
  const scopePriority = KMZ_SPEED_SCOPE_PRIORITY[useCase] || KMZ_SPEED_SCOPE_PRIORITY.playback
  const fieldName = mode === 'useGlobal'
    ? 'useGlobalSpeed'
    : (mode === 'local' ? 'waypointSpeed' : (mode === 'global' ? 'autoFlightSpeed' : ''))
  for (let i = 0; i < scopePriority.length; i += 1) {
    const scopeId = scopePriority[i]
    let paths = []
    if (mode === 'useGlobal') {
      paths = [
        scopeId + '.wpml:useGlobalSpeed',
        scopeId + '.useGlobalSpeed'
      ]
    } else if (mode === 'local') {
      paths = [
        scopeId + '.wpml:waypointSpeed',
        scopeId + '.waypointSpeed'
      ]
    } else if (mode === 'global') {
      paths = [
        scopeId + '.Folder.wpml:autoFlightSpeed',
        scopeId + '.Folder.autoFlightSpeed',
        scopeId + '.wpml:autoFlightSpeed',
        scopeId + '.autoFlightSpeed'
      ]
    }
    for (let j = 0; j < paths.length; j += 1) {
      const value = fnGetByPath(props, paths[j])
      if (value === undefined || value === null) continue
      if (typeof value === 'string' && !String(value).trim()) continue
      return {
        value: value,
        scopeId: scopeId,
        fieldName: fieldName,
        path: paths[j]
      }
    }
  }
  return {
    value: fallbackValue,
    scopeId: '',
    fieldName: fieldName,
    path: ''
  }
}

const fnResolveSpeedValueByUseCase = function (pointOrProps, useCase, mode, fallbackValue) {
  return fnResolveSpeedCandidateByUseCase(pointOrProps, useCase, mode, fallbackValue).value
}

const fnReadKmzSpeedScopeValues = function (pointOrProps, scopeId) {
  const point = (pointOrProps && pointOrProps.props && typeof pointOrProps.props === 'object') ? pointOrProps : null
  const props = point ? point.props : ((pointOrProps && typeof pointOrProps === 'object') ? pointOrProps : {})
  const safeScopeId = String(scopeId || '').trim()
  if (!safeScopeId) {
    return {
      useGlobalSpeed: null,
      waypointSpeed: null,
      autoFlightSpeed: null
    }
  }
  return {
    useGlobalSpeed: fnParseBoolLike(fnPickFirstDefined(props, [
      safeScopeId + '.wpml:useGlobalSpeed',
      safeScopeId + '.useGlobalSpeed'
    ])),
    waypointSpeed: fnToFiniteNumber(fnPickFirstDefined(props, [
      safeScopeId + '.wpml:waypointSpeed',
      safeScopeId + '.waypointSpeed'
    ])),
    autoFlightSpeed: fnToFiniteNumber(fnPickFirstDefined(props, [
      safeScopeId + '.Folder.wpml:autoFlightSpeed',
      safeScopeId + '.Folder.autoFlightSpeed',
      safeScopeId + '.wpml:autoFlightSpeed',
      safeScopeId + '.autoFlightSpeed'
    ]))
  }
}

const resolveKmzPlaybackSpeedInfo = function (pointOrProps) {
  const scopePriority = (KMZ_SPEED_SCOPE_PRIORITY.playback || []).slice()
  const useGlobalCandidate = fnResolveSpeedCandidateByUseCase(pointOrProps, 'playback', 'useGlobal', null)
  const localCandidate = fnResolveSpeedCandidateByUseCase(pointOrProps, 'playback', 'local', null)
  const globalCandidate = fnResolveSpeedCandidateByUseCase(pointOrProps, 'playback', 'global', null)
  const useGlobalSpeed = fnParseBoolLike(useGlobalCandidate.value)
  const localSpeed = fnToFiniteNumber(localCandidate.value)
  const globalSpeed = fnToFiniteNumber(globalCandidate.value)
  let resolvedSpeedMps = null
  let resolvedScopeId = ''
  let resolvedFieldName = ''
  let decisionMode = 'default'

  if (useGlobalSpeed === true) {
    if (globalSpeed !== null) {
      resolvedSpeedMps = globalSpeed
      resolvedScopeId = globalCandidate.scopeId
      resolvedFieldName = 'autoFlightSpeed'
      decisionMode = 'global'
    } else if (localSpeed !== null) {
      resolvedSpeedMps = localSpeed
      resolvedScopeId = localCandidate.scopeId
      resolvedFieldName = 'waypointSpeed'
      decisionMode = 'globalFallbackLocal'
    }
  } else if (useGlobalSpeed === false) {
    if (localSpeed !== null) {
      resolvedSpeedMps = localSpeed
      resolvedScopeId = localCandidate.scopeId
      resolvedFieldName = 'waypointSpeed'
      decisionMode = 'local'
    } else if (globalSpeed !== null) {
      resolvedSpeedMps = globalSpeed
      resolvedScopeId = globalCandidate.scopeId
      resolvedFieldName = 'autoFlightSpeed'
      decisionMode = 'localFallbackGlobal'
    }
  } else if (localSpeed !== null) {
    resolvedSpeedMps = localSpeed
    resolvedScopeId = localCandidate.scopeId
    resolvedFieldName = 'waypointSpeed'
    decisionMode = 'unspecifiedLocal'
  } else if (globalSpeed !== null) {
    resolvedSpeedMps = globalSpeed
    resolvedScopeId = globalCandidate.scopeId
    resolvedFieldName = 'autoFlightSpeed'
    decisionMode = 'unspecifiedGlobal'
  }

  return {
    unit: 'm/s',
    resolver: 'kmzPlayback',
    scopePriority: scopePriority.concat(['default']),
    scopes: {
      template: fnReadKmzSpeedScopeValues(pointOrProps, 'template'),
      waylines: fnReadKmzSpeedScopeValues(pointOrProps, 'waylines')
    },
    resolved: {
      useGlobalSpeed: useGlobalSpeed,
      useGlobalSpeedScope: String(useGlobalCandidate.scopeId || ''),
      localSpeed: localSpeed,
      localSpeedScope: String(localCandidate.scopeId || ''),
      globalSpeed: globalSpeed,
      globalSpeedScope: String(globalCandidate.scopeId || ''),
      speedMps: resolvedSpeedMps,
      speedScope: String(resolvedScopeId || ''),
      speedField: String(resolvedFieldName || ''),
      decisionMode: decisionMode
    }
  }
}

const resolveKmzPlaybackUseGlobalSpeed = function (pointOrProps, fallbackValue) {
  return fnResolveSpeedValueByUseCase(pointOrProps, 'playback', 'useGlobal', fallbackValue)
}

const resolveKmzPlaybackLocalSpeed = function (pointOrProps, fallbackValue) {
  return fnResolveSpeedValueByUseCase(pointOrProps, 'playback', 'local', fallbackValue)
}

const resolveKmzPlaybackGlobalSpeed = function (pointOrProps, fallbackValue) {
  return fnResolveSpeedValueByUseCase(pointOrProps, 'playback', 'global', fallbackValue)
}

const applyKmzResolvedSpeedDisplayValuesToMap = function (map, point, useCase) {
  const targetMap = (map && typeof map === 'object') ? map : {}
  const safeUseCase = String(useCase || 'playback').trim()
  const useGlobalValue = fnResolveSpeedValueByUseCase(point, safeUseCase, 'useGlobal', undefined)
  const localSpeed = fnResolveSpeedValueByUseCase(point, safeUseCase, 'local', undefined)
  const globalSpeed = fnResolveSpeedValueByUseCase(point, safeUseCase, 'global', undefined)
  if (useGlobalValue !== undefined) {
    targetMap['wpml:useGlobalSpeed'] = useGlobalValue
  }
  if (localSpeed !== undefined) {
    targetMap['wpml:waypointSpeed'] = localSpeed
  }
  if (globalSpeed !== undefined) {
    targetMap['wpml:autoFlightSpeed'] = globalSpeed
  }
  return targetMap
}

export {
  buildKmzScopedSpeedPropsFromSources,
  applyKmzScopedSpeedProps,
  syncKmzPointSpeedScopesFromXml,
  syncKmzMissionSpeedScopesFromXml,
  isKmzSpeedFieldPath,
  isKmzGlobalSpeedFieldPath,
  resolveKmzPlaybackUseGlobalSpeed,
  resolveKmzPlaybackLocalSpeed,
  resolveKmzPlaybackGlobalSpeed,
  resolveKmzPlaybackSpeedInfo,
  applyKmzResolvedSpeedDisplayValuesToMap
}
